import { buildGroupProps } from '../../../../../utils/elementisUtils';
import { editMember, registerMembers } from '../../../../../api/my-groups';
import { makeUsernameUnique } from '../../../../../utils/voStudents';
import { useContext, useEffect, useRef, useState, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ModalContext from '../../../../../contexts/ModalContext';

import Alert from '@pelckmans/elementis/components/alert';
import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import TextField from '@pelckmans/elementis/components/text-field';
import {
  validateMemberFirstName,
  validateMemberLastName,
  validateMemberEmail,
  validateMemberPassword,
  validateMemberUsername,
} from '../utils/memberValidations';

const TRANSLATION_KEY = 'PAGES.MY_GROUPS.MODALS.ADD_MEMBER';

export interface Member {
  id?: string;
  firstName?: string;
  lastName?: string;
  primaryEmail?: string;
  username?: string;
  password?: string;
}

interface BaseProps {
  id: string;
}

interface AddMemberProps extends BaseProps {
  groupId: string;
  refetch: () => void;
  members: Member[];
  currentMember?: never;
  onMemberEdited?: never;
  existingUsernames?: never;
}

interface EditMemberProps extends BaseProps {
  groupId: string;
  currentMember: Member;
  refetch: () => void;
  members: Member[];
  onMemberEdited?: never;
  existingUsernames?: never;
}

interface EditMemberWithCallbackProps extends BaseProps {
  currentMember: Member;
  onMemberEdited: (member: Member) => void;
  existingUsernames: string[];
  groupId?: never;
  refetch?: never;
  members?: never;
}

type Props = AddMemberProps | EditMemberProps | EditMemberWithCallbackProps;

interface FormValues {
  firstName: string;
  lastName: string;
  email?: string;
  username: string;
  password: string;
}

const DEFAULT_VALUES: FormValues = {
  firstName: '',
  lastName: '',
  username: '',
  password: '',
};

export default function MemberModal({
  id,
  groupId,
  members,
  refetch,
  currentMember,
  onMemberEdited,
  existingUsernames,
}: Props) {
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const [showAlert, setShowAlert] = useState<'invalid' | 'retry' | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const isEditWithCallback = onMemberEdited !== undefined;

  const existingUsernamesWithoutCurrent = useMemo(() => {
    if (isEditWithCallback) {
      const index = existingUsernames.findIndex(un => un === currentMember.username);
      if (index !== -1) {
        return [...existingUsernames.slice(0, index), ...existingUsernames.slice(index + 1)];
      }
      return existingUsernames;
    }

    return members.filter(m => m.id !== currentMember?.id).map(m => m.username || '');
  }, [currentMember?.id, currentMember?.username, existingUsernames, isEditWithCallback, members]);

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
    clearErrors,
    register,
    trigger,
  } = useForm<FormValues>({
    defaultValues: currentMember
      ? {
          firstName: currentMember.firstName ?? '',
          lastName: currentMember.lastName ?? '',
          email: currentMember.primaryEmail ?? undefined,
          username: currentMember.username ?? '',
          password: currentMember.password ?? '',
        }
      : DEFAULT_VALUES,
    mode: 'onBlur',
  });

  const firstName = watch('firstName');
  const lastName = watch('lastName');
  const username = watch('username');
  const usernameManuallyEdited = useRef(false);

  useEffect(() => {
    if (isEditWithCallback) {
      // Trigger validation on mount for edit mode with callback
      // Using requestAnimationFrame to defer to next frame, avoiding act() warnings in tests
      const rafId = requestAnimationFrame(() => {
        trigger();
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [isEditWithCallback, trigger]);

  useEffect(() => {
    if (!currentMember && !usernameManuallyEdited.current && (firstName || lastName)) {
      const generatedUsername = makeUsernameUnique(firstName || '', lastName || '', existingUsernamesWithoutCurrent);
      if (generatedUsername && generatedUsername !== username) {
        setValue('username', generatedUsername, { shouldValidate: false, shouldDirty: false });
        if (firstName && lastName) {
          clearErrors('username');
        }
      }
    }

    if (username.length === 0) {
      usernameManuallyEdited.current = false;
    }
  }, [firstName, lastName, existingUsernamesWithoutCurrent, setValue, username, currentMember, clearErrors]);

  const usernameRegister = register('username', {
    validate: value => {
      const error = validateMemberUsername(value, existingUsernamesWithoutCurrent);
      return error ? t(error) : undefined;
    },
  });

  const { onChange: _, ...usernameRegisterProps } = usernameRegister;

  function handleUsernameChange(e: React.ChangeEvent<HTMLInputElement>) {
    usernameRegister.onChange(e);
    if (username !== undefined && username !== e.target.value) {
      usernameManuallyEdited.current = true;
    }
  }

  async function onSubmit(data: FormValues) {
    try {
      setIsLoading(true);

      if (currentMember) {
        if (onMemberEdited) {
          const { email, ...restData } = data;
          onMemberEdited({
            ...currentMember,
            ...restData,
            primaryEmail: email ? email : undefined,
          });
        } else {
          await editMember(groupId, currentMember.id!, { ...data, email: data.email ? data.email : undefined });
        }
      } else {
        await registerMembers(groupId, [{ ...data, email: data.email ? data.email : undefined }]);
      }

      closeModal(id);
      refetch?.();
    } catch (e) {
      setShowAlert('retry');
      setIsLoading(false);
    }
  }

  function onSubmitError() {
    setShowAlert('invalid');
  }

  function renderAlert() {
    if (showAlert === 'retry') {
      return (
        <Alert
          prominence="danger"
          actions={{
            'aria-label': undefined,
            size: 'sm',
            role: undefined,
            children: (
              <Button prominence="danger" onClick={handleSubmit(onSubmit, onSubmitError)} busy={isSubmitting}>
                {t('ALERTS.RETRY.ACTION')}
              </Button>
            ),
          }}
        >
          {t('ALERTS.RETRY.MESSAGE')}
        </Alert>
      );
    }

    return <Alert prominence="danger">{t('ALERTS.INVALID.MESSAGE')}</Alert>;
  }

  return (
    <Dialog
      id={id}
      aria-label={t(`${TRANSLATION_KEY}.A11Y_LABEL`)}
      modal
      onClose={() => closeModal(id)}
      size="sm"
      className="add-member-modal"
    >
      <Dialog.Header
        aria-label={t(`${TRANSLATION_KEY}.HEADER.CLOSE.A11Y_LABEL`)}
        onClose={() => closeModal(id)}
        title={t(`${TRANSLATION_KEY}.HEADER.TITLE_${currentMember ? 'EDIT' : 'ADD'}`, {
          name: `${currentMember?.firstName} ${currentMember?.lastName}`,
        })}
      />
      <Dialog.Body alertGroupProps={!showAlert ? undefined : buildGroupProps(renderAlert())}>
        <form
          noValidate
          id="form-add-member"
          onSubmit={handleSubmit(onSubmit, onSubmitError)}
          className="add-member-form"
        >
          <TextField
            id="firstName"
            key="firstName"
            labelText={t(`${TRANSLATION_KEY}.FORM.FIRST_NAME.LABEL`)}
            autoComplete="on"
            required
            placeholder={t(`${TRANSLATION_KEY}.FORM.FIRST_NAME.PLACEHOLDER`)}
            error={!!errors.firstName}
            errorText={errors.firstName?.message}
            {...register('firstName', {
              validate: value => {
                const error = validateMemberFirstName(value);
                return error ? t(error) : undefined;
              },
            })}
          />

          <TextField
            id="lastName"
            key="lastName"
            labelText={t(`${TRANSLATION_KEY}.FORM.LAST_NAME.LABEL`)}
            autoComplete="on"
            required
            placeholder={t(`${TRANSLATION_KEY}.FORM.LAST_NAME.PLACEHOLDER`)}
            error={!!errors.lastName}
            errorText={errors.lastName?.message}
            {...register('lastName', {
              validate: value => {
                const error = validateMemberLastName(value);
                return error ? t(error) : undefined;
              },
            })}
          />

          <TextField
            id="email"
            key="email"
            labelText={t(`${TRANSLATION_KEY}.FORM.EMAIL.LABEL`)}
            autoComplete="on"
            placeholder={t(`${TRANSLATION_KEY}.FORM.EMAIL.PLACEHOLDER`)}
            error={!!errors.email}
            errorText={errors.email?.message}
            {...register('email', {
              validate: value => {
                const error = validateMemberEmail(value);
                return error ? t(error) : undefined;
              },
            })}
          />

          <TextField
            id="username"
            key="username"
            labelText={t(`${TRANSLATION_KEY}.FORM.USERNAME.LABEL`)}
            autoComplete="on"
            required
            placeholder={t(`${TRANSLATION_KEY}.FORM.USERNAME.PLACEHOLDER`)}
            error={!!errors.username}
            errorText={errors.username?.message}
            {...usernameRegisterProps}
            onChange={handleUsernameChange}
          />

          <TextField
            id="password"
            key="password"
            labelText={t(`${TRANSLATION_KEY}.FORM.PASSWORD.LABEL`)}
            autoComplete="on"
            required
            placeholder={t(`${TRANSLATION_KEY}.FORM.PASSWORD.PLACEHOLDER`)}
            error={!!errors.password}
            errorText={errors.password?.message}
            {...register('password', {
              validate: value => {
                const error = validateMemberPassword(value);
                return error ? t(error) : undefined;
              },
            })}
          />
        </form>
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={buildGroupProps(
          <>
            <Button prominence="neutral" key="cancel" onClick={() => closeModal(id)}>
              {t('ACTIONS.CANCEL')}
            </Button>

            <Button prominence="success" key="create" type="submit" form="form-add-member" busy={isLoading}>
              {t('ACTIONS.CONFIRM')}
            </Button>
          </>,
        )}
      />
    </Dialog>
  );
}
